import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Switch,
  Divider,
  Grid,
  Select,
  Loader,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import { useAppSelector } from '../../../store/hooks';
import { IconDeviceFloppy, IconMailForward, IconInfoCircle } from '@tabler/icons-react';

interface EmailSettings {
  smtp: {
    host: string;
    port: number;
    username: string;
    password: string;
    encryption: 'none' | 'ssl' | 'tls';
  };
  fromEmail: string;
  fromName: string;
  replyTo: string;
  templates: {
    welcome: string;
    passwordReset: string;
    otp: string;
    twoFactor: string;
    inquiryCompany: string;
    inquiryCustomerConfirmation: string;
    inquiryCustomerResponse: string;
  };
  notifications: {
    newUser: boolean;
    newInquiry: boolean;
    newOrder: boolean;
    systemAlerts: boolean;
  };
  dailyReport: boolean;
  weeklyDigest: boolean;
  testEmail: string;
}

// All available template file names (from src/main/resources/templates)
const TEMPLATE_OPTIONS = [
  { value: 'welcome-email', label: 'Welcome Email' },
  { value: 'password-reset-email', label: 'Password Reset' },
  { value: 'otp-email', label: 'OTP Email' },
  { value: '2fa-email', label: '2FA Email' },
  { value: 'inquiry-company-email', label: 'Inquiry to Company' },
  { value: 'inquiry-customer-confirmation', label: 'Inquiry Customer Confirmation' },
  { value: 'inquiry-customer-response', label: 'Inquiry Customer Response' },
];

export const EmailSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  const { token } = useAppSelector((state) => state.auth);
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

  const form = useForm<EmailSettings>({
    initialValues: {
      smtp: {
        host: 'smtp.gmail.com',
        port: 587,
        username: '',
        password: '',
        encryption: 'tls',
      },
      fromEmail: '',
      fromName: 'Thalvora Exports',
      replyTo: '',
      templates: {
        welcome: 'welcome-email',
        passwordReset: 'password-reset-email',
        otp: 'otp-email',
        twoFactor: '2fa-email',
        inquiryCompany: 'inquiry-company-email',
        inquiryCustomerConfirmation: 'inquiry-customer-confirmation',
        inquiryCustomerResponse: 'inquiry-customer-response',
      },
      notifications: {
        newUser: true,
        newInquiry: true,
        newOrder: true,
        systemAlerts: true,
      },
      dailyReport: false,
      weeklyDigest: true,
      testEmail: '',
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setInitialLoading(true);
    try {
      const response = await axios.get(`${API_URL}/settings/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Map backend response (which may have extra template keys) to the form shape
      const data = response.data;
      form.setValues({
        smtp: data.smtp,
        fromEmail: data.fromEmail,
        fromName: data.fromName,
        replyTo: data.replyTo,
        templates: {
          welcome: data.templates?.welcome || 'welcome-email',
          passwordReset: data.templates?.passwordReset || 'password-reset-email',
          otp: data.templates?.otp || 'otp-email',
          twoFactor: data.templates?.twoFactor || '2fa-email',
          inquiryCompany: data.templates?.inquiryCompany || 'inquiry-company-email',
          inquiryCustomerConfirmation: data.templates?.inquiryCustomerConfirmation || 'inquiry-customer-confirmation',
          inquiryCustomerResponse: data.templates?.inquiryCustomerResponse || 'inquiry-customer-response',
        },
        notifications: data.notifications,
        dailyReport: data.dailyReport,
        weeklyDigest: data.weeklyDigest,
        testEmail: '',
      });
    } catch (error) {
      console.error('Error fetching email settings:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load email settings',
        color: 'red',
      });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values: EmailSettings) => {
    setLoading(true);
    try {
      // Send the full settings object (including templates map) to backend
      await axios.put(`${API_URL}/settings/email`, values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      notifications.show({
        title: 'Success',
        message: 'Email settings updated successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update email settings',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    if (!form.values.testEmail) {
      notifications.show({
        title: 'Error',
        message: 'Please enter a test email address',
        color: 'red',
      });
      return;
    }

    setTestLoading(true);
    try {
      await axios.post(
        `${API_URL}/settings/email/test`,
        { email: form.values.testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      notifications.show({
        title: 'Success',
        message: `Test email sent to ${form.values.testEmail}`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to send test email',
        color: 'red',
      });
    } finally {
      setTestLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader size="lg" />
      </Container>
    );
  }

  return (
    <Container size="lg">
      <Title order={2} mb="lg">Email Settings</Title>

      <Paper withBorder p="xl" radius="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <Title order={4} mb="md">SMTP Configuration</Title>
            
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="SMTP Host"
                  placeholder="smtp.gmail.com"
                  {...form.getInputProps('smtp.host')}
                  required
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="SMTP Port"
                  placeholder="587"
                  type="number"
                  {...form.getInputProps('smtp.port')}
                  required
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="SMTP Username"
                  placeholder="user@gmail.com"
                  {...form.getInputProps('smtp.username')}
                  required
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <PasswordInput
                  label="SMTP Password"
                  placeholder="Enter password"
                  {...form.getInputProps('smtp.password')}
                  required
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Encryption"
                  data={[
                    { value: 'none', label: 'None' },
                    { value: 'ssl', label: 'SSL' },
                    { value: 'tls', label: 'TLS' },
                  ]}
                  {...form.getInputProps('smtp.encryption')}
                />
              </Grid.Col>
            </Grid>

            <Divider my="md" label="Email Headers" labelPosition="center" />

            <Grid>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                  label="From Email"
                  placeholder="noreply@thalvoraexports.com"
                  {...form.getInputProps('fromEmail')}
                  required
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                  label="From Name"
                  placeholder="Thalvora Exports"
                  {...form.getInputProps('fromName')}
                  required
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                  label="Reply-To"
                  placeholder="support@thalvoraexports.com"
                  {...form.getInputProps('replyTo')}
                />
              </Grid.Col>
            </Grid>

            <Divider my="md" label="Email Templates" labelPosition="center" />

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Welcome Email Template"
                  data={TEMPLATE_OPTIONS}
                  {...form.getInputProps('templates.welcome')}
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Password Reset Template"
                  data={TEMPLATE_OPTIONS}
                  {...form.getInputProps('templates.passwordReset')}
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="OTP Email Template"
                  data={TEMPLATE_OPTIONS}
                  {...form.getInputProps('templates.otp')}
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Two-Factor Email Template"
                  data={TEMPLATE_OPTIONS}
                  {...form.getInputProps('templates.twoFactor')}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Inquiry to Company Template"
                  data={TEMPLATE_OPTIONS}
                  {...form.getInputProps('templates.inquiryCompany')}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Inquiry Customer Confirmation Template"
                  data={TEMPLATE_OPTIONS}
                  {...form.getInputProps('templates.inquiryCustomerConfirmation')}
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Inquiry Customer Response Template"
                  data={TEMPLATE_OPTIONS}
                  {...form.getInputProps('templates.inquiryCustomerResponse')}
                />
              </Grid.Col>
            </Grid>

            <Divider my="md" label="Notifications" labelPosition="center" />

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Switch
                  label="New User Registration"
                  description="Send email when new user registers"
                  {...form.getInputProps('notifications.newUser', { type: 'checkbox' })}
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Switch
                  label="New Inquiry"
                  description="Send email for new contact inquiries"
                  {...form.getInputProps('notifications.newInquiry', { type: 'checkbox' })}
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Switch
                  label="New Order"
                  description="Send email for new orders"
                  {...form.getInputProps('notifications.newOrder', { type: 'checkbox' })}
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Switch
                  label="System Alerts"
                  description="Send email for system alerts"
                  {...form.getInputProps('notifications.systemAlerts', { type: 'checkbox' })}
                />
              </Grid.Col>
            </Grid>

            <Divider my="md" label="Reports" labelPosition="center" />

            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Switch
                  label="Daily Report"
                  description="Send daily summary report"
                  {...form.getInputProps('dailyReport', { type: 'checkbox' })}
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Switch
                  label="Weekly Digest"
                  description="Send weekly digest email"
                  {...form.getInputProps('weeklyDigest', { type: 'checkbox' })}
                />
              </Grid.Col>
            </Grid>

            <Divider my="md" label="Test Configuration" labelPosition="center" />

            <Alert icon={<IconInfoCircle />} color="blue">
              Send a test email to verify your SMTP configuration
            </Alert>

            <Grid>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <TextInput
                  label="Test Email Address"
                  placeholder="Enter email to receive test"
                  {...form.getInputProps('testEmail')}
                />
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, md: 4 }} className="flex items-end">
                <Button
                  leftSection={<IconMailForward size={16} />}
                  onClick={handleTestEmail}
                  loading={testLoading}
                  fullWidth
                >
                  Send Test Email
                </Button>
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="xl">
              <Button 
                type="submit" 
                color="exportGreen" 
                loading={loading}
                leftSection={<IconDeviceFloppy size={16} />}
                size="md"
              >
                Save Email Settings
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};